/**
 * Signup email-domain gating.
 *
 * Two lists, both case-insensitive exact-match on the part after `@`:
 *
 *  - PERSONAL_EMAIL_DOMAINS: consumer/free email providers. We require
 *    work emails to reduce spam signups and so each tenant maps to a
 *    real company.
 *  - BLOCKED_COMPETITOR_DOMAINS: HR/engagement competitors we don't want
 *    casually evaluating the product through the public free tier.
 *    Easy to extend; partnerships should still go through sales.
 *
 * Client-side gating only — a determined actor can always work around
 * it. The real defense is server-side allow-listing on /signup/request-pin.
 * This list buys us friction that catches casual cases.
 */

const PERSONAL_EMAIL_DOMAINS = new Set<string>([
  // Google
  'gmail.com', 'googlemail.com',
  // Microsoft
  'hotmail.com', 'hotmail.co.uk', 'hotmail.fr', 'hotmail.de', 'hotmail.it',
  'hotmail.es', 'hotmail.com.br', 'hotmail.com.ar', 'hotmail.com.mx',
  'outlook.com', 'outlook.co.uk', 'outlook.de', 'outlook.fr', 'outlook.es',
  'outlook.it', 'outlook.com.au', 'outlook.com.br',
  'live.com', 'live.co.uk', 'live.de', 'live.fr', 'live.it', 'live.com.au',
  'live.com.mx', 'live.ca',
  'msn.com',
  // Yahoo
  'yahoo.com', 'yahoo.co.uk', 'yahoo.co.in', 'yahoo.co.jp', 'yahoo.ca',
  'yahoo.fr', 'yahoo.de', 'yahoo.es', 'yahoo.it', 'yahoo.com.br',
  'yahoo.com.mx', 'yahoo.com.ar', 'yahoo.com.au', 'yahoo.com.sg',
  'yahoo.com.ph', 'yahoo.com.hk', 'yahoo.com.tw', 'yahoo.com.vn',
  'ymail.com', 'rocketmail.com',
  // Apple
  'icloud.com', 'me.com', 'mac.com',
  // AOL / Verizon
  'aol.com', 'aim.com', 'verizon.net',
  // Proton
  'protonmail.com', 'proton.me', 'pm.me',
  // Generic
  'mail.com', 'email.com', 'inbox.com', 'tutanota.com', 'tuta.io',
  'fastmail.com', 'fastmail.fm',
  // GMX
  'gmx.com', 'gmx.us', 'gmx.net', 'gmx.de', 'gmx.at', 'gmx.ch',
  // Russia
  'yandex.com', 'yandex.ru', 'mail.ru', 'list.ru', 'inbox.ru', 'bk.ru',
  // China
  'qq.com', '163.com', '126.com', 'sina.com', 'sina.cn', 'sohu.com',
  'foxmail.com',
  // Korea / Japan
  'naver.com', 'hanmail.net', 'daum.net', 'nate.com',
  'docomo.ne.jp', 'ezweb.ne.jp', 'softbank.ne.jp',
  // Germany
  'web.de', 't-online.de', 'freenet.de',
  // France
  'orange.fr', 'sfr.fr', 'free.fr', 'laposte.net', 'wanadoo.fr',
  // Italy / Spain / Brazil
  'libero.it', 'tiscali.it', 'virgilio.it', 'alice.it',
  'terra.com.br', 'uol.com.br', 'bol.com.br', 'ig.com.br',
  // Misc free providers
  'yopmail.com', 'guerrillamail.com', 'mailinator.com', 'sharklasers.com',
  '10minutemail.com', 'getnada.com', 'temp-mail.org', 'trashmail.com',
])

/**
 * Direct competitors we want to block from self-serve signup. Add to
 * this list as new ones surface. We deliberately keep partnerships
 * (BambooHR, ATS integrations, etc.) out of the list — a sales-routed
 * conversation is the right path for those.
 */
const BLOCKED_COMPETITOR_DOMAINS = new Set<string>([
  '15five.com',
  'lattice.com',
  'cultureamp.com',
  'officevibe.com',
  'workleap.com',
  'peakon.com',
  'glintinc.com',
  'bonus.ly', 'bonusly.com',
  'workhuman.com',
  'kudos.com', 'kudosnow.com',
  'humu.com',
  'leapsome.com',
  'energage.com',
  'qualtrics.com',
  'reflektive.com',
  'betterworks.com',
  'engagedly.com',
  'tinypulse.com',
  'limeade.com',
  'achievers.com',
])

export type EmailClassification = 'ok' | 'invalid' | 'personal' | 'competitor'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Pull the lowercased domain (part after the last `@`) out of an email.
 * Returns '' when the input doesn't look like an email at all.
 */
function extractDomain(email: string): string {
  const trimmed = String(email || '').trim().toLowerCase()
  if (!EMAIL_RE.test(trimmed)) return ''
  const at = trimmed.lastIndexOf('@')
  return at === -1 ? '' : trimmed.slice(at + 1)
}

export function classifyEmail(email: string): EmailClassification {
  const domain = extractDomain(email)
  if (!domain) return 'invalid'
  if (BLOCKED_COMPETITOR_DOMAINS.has(domain)) return 'competitor'
  if (PERSONAL_EMAIL_DOMAINS.has(domain)) return 'personal'
  return 'ok'
}

export function isWorkEmail(email: string): boolean {
  return classifyEmail(email) === 'ok'
}

export function emailGateMessage(classification: EmailClassification): string {
  switch (classification) {
    case 'personal':
      return 'Please use your work email — Happily is built for teams, not individual accounts.'
    case 'competitor':
      return "We can't onboard this domain through self-serve signup. Reach out to sales@happily.ai if you'd like a demo."
    case 'invalid':
      return 'Enter a valid email address.'
    default:
      return ''
  }
}
